'use client';

import { useState, useMemo, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from '@/hooks/lib/customStore';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { loginCustomerSchema, type LoginCutomerRequest } from '@/dto/reparoly/login/request';

import GoogleLoginButton from './GoogleLoginButton';
import { sanitizeNextTarget } from '@/lib/authNavigation';
import { useLoginMutation } from '@/services/reparoly/authApi';
import { showAppLoading, hideAppLoading } from '@/app/store/appShellSlice';
import { initialRepAuthState } from '@/states/repAuthentication';
import { setLogin } from '../authenticationSlice';
import { setProfile } from '@/features/profile/profileSlice';

export default function LoginForm() {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [login, { isLoading }] = useLoginMutation();

  const nextPath = useMemo(() => sanitizeNextTarget(searchParams?.get('next') || ''), [searchParams]);
  const fromOrigin = useMemo(() => (searchParams?.get('from') || '').toLowerCase(), [searchParams]);
  const isMobilePlatform = () => {
    if (typeof window === 'undefined') return false;
    const mq = window.matchMedia?.('(max-width: 768px)').matches;
    const ua = navigator?.userAgent ?? '';
    return mq || /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  };

  const form = useForm<LoginCutomerRequest>({
    resolver: zodResolver(loginCustomerSchema(t)),
    defaultValues: {
      Username: '',
      Password: '',
    },
  });

  useEffect(() => {
    dispatch(hideAppLoading())
  }, [dispatch]);

  function resolvePathByRole(roleId?: number | null): string {
    // Regla solicitada: 1 -> /dashboard, 2 y 3 -> /app
    // {1, "root"},
    // {2, "owner"},
    // {3, "manager"},
    if (roleId === 1 || roleId === 2) return '/dashboard';
    if (roleId === 4 || roleId === 5) return '/app';
    // Fallback seguro
    return '/dashboard';
  }

  const onSubmit = async (payload: LoginCutomerRequest) => {
    try {
      dispatch(showAppLoading());
      const data = await login({
        username: payload.Username,
        password: payload.Password,
        aud: 'rep-web',
      }).unwrap();
      const user = data?.user ?? null;
      const accessToken: string | null = data?.token ?? null;
      const roleId: number | null = user?.role_id ?? null;

      // Persist minimal session info + access token for authenticated API calls
      if (accessToken) {
        dispatch(
          setLogin({
            isLogged: true,
            AccessToken: accessToken,
          }),
        );
      }

      // Redirección: prioriza `next`, si no existe usar mapeo por role_id
      let target = nextPath || resolvePathByRole(roleId);
      const mobile = isMobilePlatform();
      target = mobile ? '/app' : '/dashboard';
      router.replace(target);
    } catch (error) {
      console.error('Login API error:', error);
    } finally {
      dispatch(hideAppLoading());
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="relative flex flex-col gap-6"
        aria-busy={isLoading}
      >
        <div className="grid grid-cols-1 gap-3">
          <GoogleLoginButton className="h-12 w-full justify-center font-semibold" />
        </div>
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-300" />
          <span className="text-sm text-gray-500">o</span>
          <div className="h-px flex-1 bg-gray-300" />
        </div>

        <div className="space-y-6">
          <FormField
            control={form.control}
            name="Username"
            render={({ field }) => (
              <FormItem className="gap-0 mb-4">
                <FormLabel className="font-bold mb-1">{t('LoginPage.label-username')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('LoginPage.placeholder-username')}
                    {...field}
                    type="text"
                    minLength={2}
                    maxLength={254}
                    className="h-12 focus-visible:border-b"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage className="m-0" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="Password"
            render={({ field }) => (
              <FormItem className="gap-0">
                <FormLabel className="font-bold mb-1">{t('LoginPage.label-password')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder={t('LoginPage.placeholder-password')}
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                      minLength={8}
                      maxLength={254}
                      className="h-12"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-700 focus:outline-none"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button
          type="submit"
          className="
            h-12
            bg-primary text-primary-foreground
            transition
            hover:brightness-95
            text-lg font-bold
            active:bg-primary/65 focus:bg-primary/65
            focus-visible:ring-0 focus-visible:outline-none focus-visible:outline-offset-2
          "
          disabled={isLoading}
        >
          {isLoading
            ? t('Loading.loggingIn', { defaultValue: 'Iniciando sesión…' })
            : t('btn-login')}
        </Button>
      </form>
    </Form>
  );
}
